import React, { useEffect, useState, useContext } from 'react';
import axios from 'axios';
import { API, AuthContext } from '../App';
import Layout from '../components/Layout';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Users as UsersIcon, CalendarDays, DollarSign, CheckCircle2, XCircle, Plus, Trash2, Download } from 'lucide-react';
import { toast } from 'sonner';
import { useCurrency, formatCurrency } from '../contexts/CompanyContext';
import { CompanyContext } from '../contexts/CompanyContext';
import { generatePayslipPDF } from '../lib/pdf';
import { useT } from '../contexts/LanguageContext';

export default function HR() {
  const { user } = useContext(AuthContext);
  const { symbol } = useCurrency();
  const t = useT();
  const { company } = useContext(CompanyContext);
  const [tab, setTab] = useState('leaves');
  const [summary, setSummary] = useState(null);
  const [leaves, setLeaves] = useState([]);
  const [payroll, setPayroll] = useState([]);
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [attSummary, setAttSummary] = useState(null);
  const [attLoading, setAttLoading] = useState(false);

  const canHR = ['SUPERADMIN', 'FULL_ACCESS', 'ADMIN', 'CEO', 'MANAGER'].includes(user?.role);
  const canPayroll = canHR || user?.role === 'ACCOUNTANT';

  const fetchAll = async () => {
    try {
      const [s, l, p, u] = await Promise.all([
        canHR ? axios.get(`${API}/hr/summary`, { withCredentials: true }) : Promise.resolve({ data: null }),
        axios.get(`${API}/leave-requests`, { withCredentials: true }),
        canPayroll ? axios.get(`${API}/payroll`, { withCredentials: true }) : Promise.resolve({ data: [] }),
        canHR ? axios.get(`${API}/users`, { withCredentials: true }) : Promise.resolve({ data: [] }),
      ]);
      setSummary(s.data); setLeaves(l.data || []); setPayroll(p.data || []); setUsers(u.data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load HR data');
    }
  };

  useEffect(() => { fetchAll(); /* eslint-disable-next-line */ }, []);

  const openLeave = () => { setForm({ leave_type: 'annual', user_id: user?.id, start_date: new Date().toISOString().slice(0, 10), end_date: new Date().toISOString().slice(0, 10) }); setOpen('leave'); };
  const openPayroll = () => { const now = new Date(); setAttSummary(null); setForm({ month: now.getMonth() + 1, year: now.getFullYear(), basic_salary: 0, allowances: 0, overtime: 0, deductions: 0, status: 'pending', ot_rate_per_hour: 0, absent_deduction_per_day: 0 }); setOpen('payroll'); };

  // Auto-fetch attendance summary whenever payroll form has user/month/year selected.
  useEffect(() => {
    if (open !== 'payroll' || !form.user_id || !form.month || !form.year) {
      setAttSummary(null);
      return;
    }
    let cancelled = false;
    setAttLoading(true);
    axios.get(`${API}/payroll/attendance-summary`, {
      params: { user_id: form.user_id, month: form.month, year: form.year },
      withCredentials: true,
    }).then(r => { if (!cancelled) setAttSummary(r.data); })
      .catch(() => { if (!cancelled) setAttSummary(null); })
      .finally(() => { if (!cancelled) setAttLoading(false); });
    return () => { cancelled = true; };
  }, [open, form.user_id, form.month, form.year]);

  const applyAttendanceToPayroll = () => {
    if (!attSummary) return;
    const otRate = parseFloat(form.ot_rate_per_hour) || 0;
    const absentRate = parseFloat(form.absent_deduction_per_day) || 0;
    const computedOT = +(attSummary.overtime_hours * otRate).toFixed(2);
    const computedDeduction = +(attSummary.absent_days * absentRate).toFixed(2);
    setForm(f => ({
      ...f,
      overtime: computedOT,
      deductions: computedDeduction,
    }));
    toast.success(`Applied: OT ${attSummary.overtime_hours}h × ${otRate} = ${computedOT}, Absent ${attSummary.absent_days}d × ${absentRate} = ${computedDeduction}`);
  };

  const save = async () => {
    setSaving(true);
    try {
      if (open === 'leave') {
        await axios.post(`${API}/leave-requests`, form, { withCredentials: true });
        toast.success('Leave request submitted');
      } else if (open === 'payroll') {
        // Strip helper fields that the backend model doesn't store.
        const { ot_rate_per_hour: _ot, absent_deduction_per_day: _ad, ...payload } = form;
        void _ot; void _ad;
        await axios.post(`${API}/payroll`, payload, { withCredentials: true });
        toast.success('Payroll record created');
      }
      setOpen(null); fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
    setSaving(false);
  };

  const decide = async (id, decision) => {
    try {
      await axios.put(`${API}/leave-requests/${id}/decision`, { decision }, { withCredentials: true });
      toast.success(`Leave ${decision}`);
      fetchAll();
    } catch (e) { toast.error('Action failed'); }
  };

  const updatePayrollStatus = async (id, status) => {
    try {
      await axios.put(`${API}/payroll/${id}`, { status }, { withCredentials: true });
      toast.success(`Payroll marked ${status}`);
      fetchAll();
    } catch (e) { toast.error('Action failed'); }
  };

  const removeItem = async (kind, id) => {
    if (!window.confirm('Delete this record?')) return;
    try {
      await axios.delete(`${API}/${kind}/${id}`, { withCredentials: true });
      toast.success('Deleted'); fetchAll();
    } catch (e) { toast.error('Delete failed'); }
  };

  const downloadPayslip = (p) => {
    try {
      generatePayslipPDF({ payroll: p, company });
    } catch (e) {
      toast.error('PDF generation failed');
    }
  };

  const statusColor = (s) => ({
    pending: 'bg-amber-100 text-amber-700',
    approved: 'bg-emerald-100 text-emerald-700',
    rejected: 'bg-rose-100 text-rose-700',
    cancelled: 'bg-slate-100 text-slate-600',
    paid: 'bg-emerald-100 text-emerald-700',
  })[s] || 'bg-slate-100 text-slate-700';

  const Tab = ({ id, label, count }) => (
    <button data-testid={`hr-tab-${id}`} onClick={() => setTab(id)}
      className={`px-4 py-2 rounded-lg text-sm font-medium transition ${tab === id ? 'bg-amber-500 text-slate-900 shadow' : 'bg-card text-foreground hover:bg-muted border border-border'}`}>
      {label} {count != null && <span className="ml-1 text-xs opacity-70">({count})</span>}
    </button>
  );

  return (
    <Layout>
      <div data-testid="hr-page" className="space-y-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-foreground flex items-center gap-2">
              <UsersIcon className="w-6 h-6 text-amber-600" />{t('nav.hr') === 'nav.hr' ? 'Human Resources' : t('nav.hr')}
            </h1>
            <p className="text-sm text-muted-foreground">{t('hr.subtitle') === 'hr.subtitle' ? 'Leave requests, payroll, and people operations.' : t('hr.subtitle')}</p>
          </div>
          <div className="flex gap-2">
            <Button data-testid="new-leave-btn" onClick={openLeave} className="bg-amber-500 hover:bg-amber-600 text-slate-900"><Plus className="w-4 h-4 mr-1" />{t('hr.requestLeave')}</Button>
            {canPayroll && <Button data-testid="new-payroll-btn" onClick={openPayroll} variant="outline"><Plus className="w-4 h-4 mr-1" />{t('hr.newPayroll')}</Button>}
          </div>
        </div>

        {summary && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <Card className="p-4 bg-gradient-to-br from-amber-50 to-yellow-50 border border-amber-200">
              <div className="text-xs uppercase tracking-wide text-amber-700">{t('hr.pendingLeaves')}</div>
              <div className="text-2xl font-bold text-amber-900 mt-1">{summary.leaves.pending}</div>
              <div className="text-xs text-amber-700 mt-1">{t('hr.awaitingDecision')}</div>
            </Card>
            <Card className="p-4 bg-gradient-to-br from-emerald-50 to-teal-50 border border-emerald-200">
              <div className="text-xs uppercase tracking-wide text-emerald-700">{t('hr.approvedYtd')}</div>
              <div className="text-2xl font-bold text-emerald-900 mt-1">{summary.leaves.approved_total_days}</div>
              <div className="text-xs text-emerald-700 mt-1">{t('hr.totalRequests', { count: summary.leaves.total_requests })}</div>
            </Card>
            <Card className="p-4 bg-gradient-to-br from-sky-50 to-indigo-50 border border-sky-200">
              <div className="text-xs uppercase tracking-wide text-sky-700">{t('hr.payrollYtd')}</div>
              <div className="text-2xl font-bold text-sky-900 mt-1">{formatCurrency(summary.payroll.ytd_paid, symbol)}</div>
              <div className="text-xs text-sky-700 mt-1">{t('hr.recordsThisYear', { count: summary.payroll.records_this_year })}</div>
            </Card>
            <Card className="p-4 bg-gradient-to-br from-rose-50 to-pink-50 border border-rose-200">
              <div className="text-xs uppercase tracking-wide text-rose-700">{t('hr.pendingPayroll')}</div>
              <div className="text-2xl font-bold text-rose-900 mt-1">{summary.payroll.pending_approval}</div>
              <div className="text-xs text-rose-700 mt-1">{t('hr.awaitingApproval')}</div>
            </Card>
          </div>
        )}

        <div className="flex flex-wrap gap-2 border-b border-slate-200 pb-2">
          <Tab id="leaves" label={t('tab.leaves')} count={leaves.length} />
          {canPayroll && <Tab id="payroll" label={t('tab.payroll')} count={payroll.length} />}
        </div>

        {tab === 'leaves' && (
          <Card className="overflow-hidden">
            {leaves.length === 0 ? <div className="py-12 text-center text-muted-foreground">{t('hr.noLeaves')}</div> :
             <div className="overflow-x-auto">
               <table className="w-full text-sm">
                 <thead className="bg-slate-50"><tr className="text-left text-xs text-muted-foreground uppercase">
                   <th className="px-4 py-2">{t('th.employee')}</th><th className="px-4 py-2">{t('th.type')}</th><th className="px-4 py-2">{t('th.from')}</th><th className="px-4 py-2">{t('th.to')}</th><th className="px-4 py-2">{t('th.days')}</th><th className="px-4 py-2">{t('th.reason')}</th><th className="px-4 py-2">{t('th.status')}</th><th className="px-4 py-2"></th>
                 </tr></thead>
                 <tbody className="divide-y divide-slate-100">
                   {leaves.map((l, idx) => (
                     <tr key={l.id} data-testid={`leave-row-${idx}`} className="hover:bg-slate-50">
                       <td className="px-4 py-2">{l.user_name || '—'}</td>
                       <td className="px-4 py-2">{(() => { const x = t(`hr.leaveTypes.${l.leave_type}`); return x === `hr.leaveTypes.${l.leave_type}` ? l.leave_type : x; })()}</td>
                       <td className="px-4 py-2">{l.start_date}</td>
                       <td className="px-4 py-2">{l.end_date}</td>
                       <td className="px-4 py-2">{l.days}</td>
                       <td className="px-4 py-2 max-w-[200px] truncate" title={l.reason}>{l.reason || '—'}</td>
                       <td className="px-4 py-2"><span className={`text-xs px-2 py-0.5 rounded ${statusColor(l.status)}`}>{(() => { const x = t(`status.${l.status}`); return x === `status.${l.status}` ? l.status : x; })()}</span></td>
                       <td className="px-4 py-2 text-right">
                         {canHR && l.status === 'pending' && <>
                           <Button data-testid={`leave-approve-${idx}`} size="sm" variant="outline" className="text-emerald-700" onClick={() => decide(l.id, 'approved')}><CheckCircle2 className="w-3.5 h-3.5" /></Button>
                           <Button data-testid={`leave-reject-${idx}`} size="sm" variant="outline" className="text-rose-700 ml-1" onClick={() => decide(l.id, 'rejected')}><XCircle className="w-3.5 h-3.5" /></Button>
                         </>}
                         <Button size="sm" variant="ghost" className="text-rose-600 ml-1" onClick={() => removeItem('leave-requests', l.id)}><Trash2 className="w-3.5 h-3.5" /></Button>
                       </td>
                     </tr>
                   ))}
                 </tbody>
               </table>
             </div>}
          </Card>
        )}

        {tab === 'payroll' && canPayroll && (
          <Card className="overflow-hidden">
            {payroll.length === 0 ? <div className="py-12 text-center text-muted-foreground">{t('hr.noPayroll')}</div> :
             <div className="overflow-x-auto">
               <table className="w-full text-sm">
                 <thead className="bg-slate-50"><tr className="text-left text-xs text-muted-foreground uppercase">
                   <th className="px-4 py-2">{t('th.employee')}</th><th className="px-4 py-2">{t('th.monthYear')}</th><th className="px-4 py-2 text-right">{t('th.basic')}</th><th className="px-4 py-2 text-right">{t('th.allowances')}</th><th className="px-4 py-2 text-right">{t('th.ot')}</th><th className="px-4 py-2 text-right">{t('th.deductions')}</th><th className="px-4 py-2 text-right">{t('th.net')}</th><th className="px-4 py-2">{t('th.status')}</th><th className="px-4 py-2"></th>
                 </tr></thead>
                 <tbody className="divide-y divide-slate-100">
                   {payroll.map((p, idx) => (
                     <tr key={p.id} data-testid={`payroll-row-${idx}`} className="hover:bg-slate-50">
                       <td className="px-4 py-2">{p.user_name || '—'}</td>
                       <td className="px-4 py-2">{p.month}/{p.year}</td>
                       <td className="px-4 py-2 text-right">{formatCurrency(p.basic_salary, symbol)}</td>
                       <td className="px-4 py-2 text-right">{formatCurrency(p.allowances, symbol)}</td>
                       <td className="px-4 py-2 text-right">{formatCurrency(p.overtime, symbol)}</td>
                       <td className="px-4 py-2 text-right text-rose-600">-{formatCurrency(p.deductions, symbol)}</td>
                       <td className="px-4 py-2 text-right font-bold">{formatCurrency(p.net_salary, symbol)}</td>
                       <td className="px-4 py-2"><span className={`text-xs px-2 py-0.5 rounded ${statusColor(p.status)}`}>{(() => { const x = t(`status.${p.status}`); return x === `status.${p.status}` ? p.status : x; })()}</span></td>
                       <td className="px-4 py-2 text-right">
                         <Button data-testid={`payslip-pdf-${idx}`} size="sm" variant="outline" onClick={() => downloadPayslip(p)} title={t('common.downloadPdf')}><Download className="w-3.5 h-3.5" /></Button>
                         {p.status !== 'paid' && <Button data-testid={`payroll-pay-${idx}`} size="sm" variant="outline" className="text-emerald-700 ml-1" onClick={() => updatePayrollStatus(p.id, 'paid')}>{t('accounting.markPaid')}</Button>}
                         {canHR && <Button size="sm" variant="ghost" className="text-rose-600 ml-1" onClick={() => removeItem('payroll', p.id)}><Trash2 className="w-3.5 h-3.5" /></Button>}
                       </td>
                     </tr>
                   ))}
                 </tbody>
               </table>
             </div>}
          </Card>
        )}

        <Dialog open={!!open} onOpenChange={(v) => !v && setOpen(null)}>
          <DialogContent className="max-w-lg">
            <DialogHeader><DialogTitle>{open === 'leave' ? t('hr.requestLeaveTitle') : t('hr.newPayrollTitle')}</DialogTitle></DialogHeader>
            <div className="space-y-3">
              {open === 'leave' && <>
                {canHR && users.length > 0 && (
                  <div><Label>{t('th.employee')}</Label>
                    <Select value={form.user_id || ''} onValueChange={(v) => setForm({ ...form, user_id: v })}>
                      <SelectTrigger data-testid="leave-employee-select"><SelectValue /></SelectTrigger>
                      <SelectContent>{users.map(u => <SelectItem key={u.id} value={u.id}>{u.name} ({u.role})</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                )}
                <div><Label>{t('hr.leaveType')}</Label>
                  <Select value={form.leave_type || 'annual'} onValueChange={(v) => setForm({ ...form, leave_type: v })}>
                    <SelectTrigger data-testid="leave-type-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="annual">{t('hr.leaveTypes.annual')}</SelectItem>
                      <SelectItem value="sick">{t('hr.leaveTypes.sick')}</SelectItem>
                      <SelectItem value="unpaid">{t('hr.leaveTypes.unpaid')}</SelectItem>
                      <SelectItem value="casual">{t('hr.leaveTypes.casual')}</SelectItem>
                      <SelectItem value="other">{t('hr.leaveTypes.other')}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div><Label>{t('th.from')}</Label><Input type="date" data-testid="leave-start" value={form.start_date || ''} onChange={(e) => setForm({ ...form, start_date: e.target.value })} /></div>
                  <div><Label>{t('th.to')}</Label><Input type="date" data-testid="leave-end" value={form.end_date || ''} onChange={(e) => setForm({ ...form, end_date: e.target.value })} /></div>
                </div>
                <div><Label>{t('hr.reason')}</Label><Textarea rows={3} value={form.reason || ''} onChange={(e) => setForm({ ...form, reason: e.target.value })} /></div>
              </>}
              {open === 'payroll' && <>
                <div><Label>{t('th.employee')}</Label>
                  <Select value={form.user_id || ''} onValueChange={(v) => setForm({ ...form, user_id: v })}>
                    <SelectTrigger data-testid="payroll-employee-select"><SelectValue /></SelectTrigger>
                    <SelectContent>{users.map(u => <SelectItem key={u.id} value={u.id}>{u.name} ({u.role})</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div><Label>{t('hr.month')}</Label><Input type="number" min="1" max="12" value={form.month ?? ''} onChange={(e) => setForm({ ...form, month: parseInt(e.target.value) || 1 })} /></div>
                  <div><Label>{t('hr.year')}</Label><Input type="number" value={form.year ?? ''} onChange={(e) => setForm({ ...form, year: parseInt(e.target.value) || 2026 })} /></div>
                </div>

                {/* Attendance summary auto-pulled from Attendance module for this employee/month */}
                <div data-testid="payroll-attendance-summary" className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-xs">
                  <div className="font-semibold text-emerald-800 mb-1.5 flex items-center justify-between">
                    <span>Attendance for {form.month}/{form.year}</span>
                    {attLoading && <span className="text-emerald-600">Loading…</span>}
                  </div>
                  {!form.user_id ? (
                    <p className="text-slate-600">Select an employee above to load attendance.</p>
                  ) : !attSummary ? (
                    <p className="text-slate-600">{attLoading ? 'Loading attendance…' : 'No attendance data.'}</p>
                  ) : !attSummary.linked ? (
                    <p className="text-amber-700">⚠ This user is not linked to a Staff record. Open <strong>Staff</strong> and set the user_id on their Staff profile so attendance flows to payroll.</p>
                  ) : (
                    <>
                      <div className="grid grid-cols-3 gap-2 text-slate-700">
                        <div><div className="text-[10px] uppercase tracking-wide text-emerald-700">Present</div><div className="font-semibold text-base" data-testid="att-present">{attSummary.present_days}</div></div>
                        <div><div className="text-[10px] uppercase tracking-wide text-emerald-700">Absent</div><div className="font-semibold text-base" data-testid="att-absent">{attSummary.absent_days}</div></div>
                        <div><div className="text-[10px] uppercase tracking-wide text-emerald-700">Leave / Half</div><div className="font-semibold text-base">{attSummary.leave_days} / {attSummary.half_days}</div></div>
                        <div><div className="text-[10px] uppercase tracking-wide text-emerald-700">Worked Hrs</div><div className="font-semibold text-base" data-testid="att-hours">{attSummary.worked_hours}</div></div>
                        <div><div className="text-[10px] uppercase tracking-wide text-emerald-700">OT Hrs</div><div className="font-semibold text-base" data-testid="att-ot">{attSummary.overtime_hours}</div></div>
                        <div><div className="text-[10px] uppercase tracking-wide text-emerald-700">Records</div><div className="font-semibold text-base">{attSummary.records}</div></div>
                      </div>
                      <div className="mt-2.5 grid grid-cols-2 gap-2 items-end">
                        <div><Label className="text-[11px]">OT rate / hour ({symbol})</Label><Input type="number" min="0" step="0.5" value={form.ot_rate_per_hour ?? 0} onChange={(e) => setForm({ ...form, ot_rate_per_hour: parseFloat(e.target.value) || 0 })} className="h-8" /></div>
                        <div><Label className="text-[11px]">Absent deduction / day ({symbol})</Label><Input type="number" min="0" step="0.5" value={form.absent_deduction_per_day ?? 0} onChange={(e) => setForm({ ...form, absent_deduction_per_day: parseFloat(e.target.value) || 0 })} className="h-8" /></div>
                      </div>
                      <Button data-testid="apply-attendance-to-payroll" type="button" size="sm" variant="outline" onClick={applyAttendanceToPayroll} className="mt-2 border-emerald-400 text-emerald-700 hover:bg-emerald-100 h-8 text-xs">
                        Auto-fill Overtime & Deductions from Attendance
                      </Button>
                    </>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-2">
                  <div><Label>{t('hr.basicSalary')}</Label><Input type="number" data-testid="payroll-basic" value={form.basic_salary ?? ''} onChange={(e) => setForm({ ...form, basic_salary: parseFloat(e.target.value) || 0 })} /></div>
                  <div><Label>{t('th.allowances')}</Label><Input type="number" value={form.allowances ?? ''} onChange={(e) => setForm({ ...form, allowances: parseFloat(e.target.value) || 0 })} /></div>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div><Label>{t('th.ot')}</Label><Input type="number" value={form.overtime ?? ''} onChange={(e) => setForm({ ...form, overtime: parseFloat(e.target.value) || 0 })} /></div>
                  <div><Label>{t('th.deductions')}</Label><Input type="number" value={form.deductions ?? ''} onChange={(e) => setForm({ ...form, deductions: parseFloat(e.target.value) || 0 })} /></div>
                </div>
                <div className="text-xs text-slate-600">{t('hr.netSalaryPreview')}<strong>{formatCurrency((form.basic_salary || 0) + (form.allowances || 0) + (form.overtime || 0) - (form.deductions || 0), symbol)}</strong></div>
              </>}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(null)}>{t('common.cancel')}</Button>
              <Button data-testid="hr-save-btn" onClick={save} disabled={saving} className="bg-amber-500 hover:bg-amber-600 text-slate-900">
                {saving ? t('hr.saving') : t('common.save')}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
